import type { JSX, PropsWithChildren } from "react";

import { Text } from "@/ui/core";
import { Box, HStack, VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface BaseChannelPreviewCardContainerProps extends PropsWithChildren<ViewWithTestID> {
  avatarComponent: JSX.Element | null;
  onPress: () => Promise<void> | void;
}

const BaseChannelPreviewCardContainer = ({
  avatarComponent,
  children,
  onPress,
  testID,
}: BaseChannelPreviewCardContainerProps) => {
  return (
    <HStack
      gap={16}
      onPress={() => void onPress()}
      paddingHorizontal={12}
      paddingVertical={14}
      pressStyle={{
        backgroundColor: ColorToken.NeutralLine,
        borderRadius: 8,
      }}
      testID={testID}
      width="100%"
    >
      {avatarComponent}
      <VStack flex={1} gap={2}>
        {children}
      </VStack>
    </HStack>
  );
};

const UnreadIndicator = ({ testID }: BaseViewProps) => (
  <Box
    backgroundColor={ColorToken.TertiaryMarigoldBase}
    borderRadius={6}
    height={6}
    testID={joinTestIDs(testID, "unread-indicator")}
    width={6}
  />
);

const Title = ({ children, testID }: PropsWithChildren<ViewWithTestID>) => {
  return (
    <Text
      color={ColorToken.NeutralPrimary}
      flexShrink={0}
      numberOfLines={1}
      testID={testID}
      variant="bodyMedium"
    >
      {children}
    </Text>
  );
};

const Description = ({
  children,
  testID,
}: PropsWithChildren<ViewWithTestID>) => (
  <Text
    color={ColorToken.NeutralSecondary}
    flexShrink={1}
    numberOfLines={1}
    testID={testID}
    variant="captionMedium"
  >
    {children}
  </Text>
);

export { BaseChannelPreviewCardContainer, Description, Title, UnreadIndicator };
